import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { useAnalyticsOrgLocations } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  Button,
  emptyStateGraphics,
  SearchField,
  useFormField,
  useModalControl,
  Text,
} from '@frontend/design-system';
import { PhoneAnalyticsFilters } from '../components/filters/phone-analytics-filters';
import { PhoneTreeDetailPanel } from '../components/phone-tree-call-volume-panel';
import { DEFAULT_ACTIVE_TAB_VIEW } from '../constants';
import { useFetchAggregatedPhoneTreeCallVolume } from '../hooks/use-fetch-aggregated-phone-tree-call-volume';
import { useGetChartPayloadAndConfig } from '../hooks/use-get-chart-payload-and-config';
import { useOnPhoneAnalyticsPageLoad } from '../hooks/use-on-phone-analytics-page-load';
import { usePhoneAnalyticsShallowStore } from '../hooks/use-phone-analytics-store';
import { PhoneTreeCallVolumeChart } from '../overview-cards/phone-tree-call-volume';
import { PhoneTreePhoneNumbersTrayType } from '../shared-types';
import { transformChartData } from '../utils/transform-phone-tree-chart-data';

const ITEMS_PER_PAGE = 6;

export const AllPhoneTreeCallVolumeChart = () => {
  const { t } = useTranslation('analytics-phone');

  const { filters, isDemoAccount, previousGlsLocationIds, demoData } = usePhoneAnalyticsShallowStore(
    'filterHintText',
    'filters',
    'isDemoAccount',
    'previousGlsLocationIds',
    'demoData'
  );

  const { locationNames } = useAnalyticsOrgLocations({
    isAllAccessScopeEnabled: true,
    isDemoAccount,
    module: 'PHONE',
  });

  const { applicableFilters } = useGetChartPayloadAndConfig({
    filters,
    isDemoAccount,
    previousGlsLocationIds,
    uniqueId: 'routing-analytics',
  });
  const searchFieldProps = useFormField({ type: 'text' });

  const { data, allPhoneNumbers } = useFetchAggregatedPhoneTreeCallVolume({
    filters,
    isDemoAccount,
    previousGlsLocationIds,
    demoData,
  });

  const transformedData = useMemo(
    () => transformChartData({ data, allPhoneNumbers, locationNames }),
    [allPhoneNumbers, data, locationNames]
  );

  // Track data version to force chart remounts when underlying data changes
  const dataVersionRef = useRef(0);
  const prevTransformedDataRef = useRef(transformedData);
  if (prevTransformedDataRef.current !== transformedData) {
    prevTransformedDataRef.current = transformedData;
    dataVersionRef.current += 1;
  }

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  // Reset pagination when underlying data changes (e.g., filters changed)
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [transformedData]);

  // Derive filtered data from transformedData and search value
  const filteredData = useMemo(() => {
    const searchValue = searchFieldProps.value?.toLowerCase() ?? '';
    if (!searchValue) return null;
    return transformedData?.filter((d) => d.phoneTreeName?.toLowerCase().includes(searchValue)) ?? [];
  }, [transformedData, searchFieldProps.value]);

  const onSearchOnChange = useCallback(
    (e: any) => {
      searchFieldProps.onChange(e);
      setVisibleCount(ITEMS_PER_PAGE);
    },
    [searchFieldProps]
  );

  const dataToRender = useMemo(() => {
    const dataSource = filteredData ?? transformedData;
    return dataSource?.slice(0, visibleCount) ?? [];
  }, [filteredData, transformedData, visibleCount]);

  const onLoadMore = () => {
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  };

  const { pageSubtitle } = useOnPhoneAnalyticsPageLoad();
  const { modalProps, triggerProps } = useModalControl();

  const [modalData, setModalData] = useState<PhoneTreePhoneNumbersTrayType>({
    phoneTreeName: '',
    callRouteId: '',
    callRouteName: '',
    phoneNumbers: [],
  });

  const minDate = new Date('2025-09-01');
  const startDate = applicableFilters.start_date ? new Date(applicableFilters.start_date) : undefined;
  const isDateValid = startDate ? startDate >= minDate : false;

  const showNoDataAvailable = data?.length === 0 || !isDateValid;
  const EmptyStateGraphicAnalytics = emptyStateGraphics.analytics;

  const hasMoreToLoad = !showNoDataAvailable && (filteredData ?? transformedData)?.length > visibleCount;

  return (
    <Page>
      <Page.Header>
        <Page.Header.Breadcrumbs
          breadcrumbs={[
            { label: t('Phone Analytics'), to: '/analytics/phone/main' },
            { label: t('Phone Tree Call Volume') },
          ]}
        />
        <Page.Header.Title title={t('Phone Tree Call Volume')} />
        <Page.Header.Subtitle
          subtitle={t('Showing results for {{filtersInfo}}', {
            filtersInfo: pageSubtitle,
          })}
        />
      </Page.Header>

      <Page.Body>
        <PhoneAnalyticsFilters activeTab={DEFAULT_ACTIVE_TAB_VIEW}>
          <SearchField name='phone-tree-call-volume-search' {...searchFieldProps} onChange={onSearchOnChange} />
        </PhoneAnalyticsFilters>

        {showNoDataAvailable ? (
          <>
            {!isDateValid && (
              <BannerNotification
                status='warn'
                message={t('Phone tree data is only available from 9/1/2025 onward. Please select a valid date range.')}
              />
            )}
            <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, marginTop: 32 }}>
              <EmptyStateGraphicAnalytics title={t('No data available')} width={200} height={200} />
              <Text color='light'>{t('No data available')}</Text>
            </div>
          </>
        ) : (
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: ${theme.spacing(2)};
              width: 100%;
            `}
          >
            {dataToRender.map(({ phoneTreeId, ...rest }) => (
              <PhoneTreeCallVolumeChart
                key={`${phoneTreeId}-${dataVersionRef.current}`}
                phoneTreeId={phoneTreeId}
                triggerProps={triggerProps}
                setModalData={setModalData}
                emptyStateConfig={{
                  type: 'analytics',
                  label: t('No data available'),
                }}
                style={{ marginBottom: 0 }}
                {...rest}
              />
            ))}
          </div>
        )}

        {hasMoreToLoad && (
          <div
            css={{
              margin: `${theme.spacing(2)} auto`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button variant='tertiary' label='Load More' onClick={onLoadMore}>
              {t('Load More')}
            </Button>
          </div>
        )}
      </Page.Body>
      {modalProps.show && <PhoneTreeDetailPanel onClose={modalProps.onClose} modalData={modalData} />}
    </Page>
  );
};
